import { defineStore } from 'pinia';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';
import { useApiStore } from 'src/core/stores/api';
import { EnumType, FormatedEnum } from 'src/core/types/enum';
import { ResponseDatasetEnums } from 'src/core/types/enum/dataset';

type DatasetEnumsStore = {
  enums: ResponseDatasetEnums;
  formatedEnums: FormatedEnum[];
};

/**
 * A store for managing dataset enums data.
 */
export const useDatasetEnumsStore = defineStore('datasetEnums', {
  state: () =>
    ({
      enums: {} as ResponseDatasetEnums,
      formatedEnums: [] as FormatedEnum[],
    } as DatasetEnumsStore),

  getters: {
    /**
     * Get the formatted value of an enum.
     * @param enumVal - The enum value to format.
     * @returns The formatted enum value.
     */
    getFormatedEnumValue: (state) => (enumVal: string) => {
      return (
        state.formatedEnums.find(
          (enumValue: FormatedEnum) => enumValue[enumVal]
        )?.[enumVal] || enumVal
      );
    },

    /**
     * Get the enum instance based on the enum value.
     * @param enumVal - The enum value to search for.
     * @returns The enum value if found, undefined otherwise.
     */
    getEnumInstance: (state) => (enumVal: string) => {
      let foundEnum: EnumType | undefined;
      Object.keys(state.enums).forEach((key: string) => {
        if (foundEnum) return;
        foundEnum = state.enums[key as keyof typeof state.enums].find(
          (enumValue) => enumValue.value === enumVal
        );
      });
      return foundEnum;
    },
  },

  actions: {
    /**
     * Get enums information.
     */
    async getEnums() {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const responseEnums = await apiStore.service.get<ResponseDatasetEnums>(
          '/dataset/enums'
        );
        const responseEnumsSorted = Object.fromEntries(
          Object.entries(responseEnums.data).map(([key, value]) => [
            key,
            value.sort((a, b) => a.label.localeCompare(b.label)),
          ])
        );
        this.formatedEnums = [];
        this.enums = responseEnumsSorted;

        Object.keys(responseEnumsSorted).forEach((key: string) => {
          responseEnumsSorted[key as keyof ResponseDatasetEnums].forEach(
            (enumValue: EnumType) => {
              this.formatedEnums.push({ [enumValue.value]: enumValue.label });
            }
          );
        });
      } catch (e) {
        handleError('Enums error', e);
      }
    },
  },
});
